import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { CalendarClock, Mic, Plus } from 'lucide-react'
import { Button, Card, EmptyState, Modal, Pagination, SkeletonList } from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { useT } from '@/internationalization/i18n'

import {
  CAPABILITIES,
  PERMISSIONS,
  useCapability,
  usePermissions,
} from '@/features/authorization'

import { DeviceRecorderDialog } from './components/DeviceRecorderDialog'
import { MeetingFilters } from './components/MeetingFilters'
import { MeetingListItem } from './components/MeetingListItem'
import { useMeetings, useRecordMeeting } from './hooks'
import { buildMeetingSearchParams, type MeetingListParams } from './api'

const PAGE_SIZE = 20

type RecordingPlatform = 'teams' | 'meet' | 'zoom'

export function MeetingsPage() {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isRecordDialogOpen, setIsRecordDialogOpen] = useState(false)
  const [platform, setPlatform] = useState<RecordingPlatform>('teams')
  const [meetingUrl, setMeetingUrl] = useState('')
  const [meetingTitle, setMeetingTitle] = useState('')
  const { has } = usePermissions()
  const recordingAvailable = useCapability(CAPABILITIES.manualRecording)
  const deviceRecordingAvailable = useCapability(CAPABILITIES.deviceRecording)
  const [isDeviceDialogOpen, setIsDeviceDialogOpen] = useState(false)
  const recordMeeting = useRecordMeeting()

  const params: MeetingListParams = {
    q: searchParams.get('q') ?? '',
    status: searchParams.get('status') ?? 'all',
    leaveReason: searchParams.get('leaveReason') ?? 'all',
    platform: searchParams.get('platform') ?? 'all',
    patron: searchParams.get('patron') ?? 'all',
    page: Number(searchParams.get('page') ?? 1),
    pageSize: PAGE_SIZE,
  }

  const patch = (next: Partial<MeetingListParams>) => {
    setSearchParams(buildMeetingSearchParams(params, next), { replace: true })
  }

  const { data, isPending, error, refetch } = useMeetings(params)
  // A search or filter means the person has already pinpointed a specific
  // meeting (or a narrowed set) -- redirecting a recurring match to the
  // series's own unfiltered, unhighlighted, newest-first list would bury the
  // very meeting they just found. The series redirect is only for a plain,
  // unfiltered browse of the list.
  const isPlainBrowse =
    (params.q ?? '').trim() === '' &&
    params.status === 'all' &&
    params.leaveReason === 'all' &&
    params.platform === 'all' &&
    params.patron === 'all'
  // BOTH, and they are different questions. The permission says this
  // person is allowed to ask for a recording; the capability says this
  // deployment has a recorder to dispatch. Gating on the permission
  // alone offered a button that answered 503 in every environment where
  // the ECS wiring was absent -- and because the permission is granted
  // by a MIGRATION, that switch is thrown database-wide rather than per
  // deployment. Hidden rather than disabled: there is nothing the person
  // can do about an unwired environment, so a disabled control with a
  // tooltip would only be a puzzle.
  const canRecord = has(PERMISSIONS.meetingRecord) && recordingAvailable
  // "Record now" is the same permission -- asking for a recording -- behind
  // its own deployment switch, since it needs no recorder at all.
  const canRecordNow = has(PERMISSIONS.meetingRecord) && deviceRecordingAvailable

  const closeRecordDialog = () => {
    setIsRecordDialogOpen(false)
    setMeetingUrl('')
    setMeetingTitle('')
    setPlatform('teams')
  }

  return (
    <div>
      <PageHeader
        title={t('meetings.title')}
        description={t('meetings.description')}
        actions={
          (canRecord || canRecordNow) && (
            <>
              {canRecordNow && (
                <Button
                  variant={canRecord ? 'secondary' : 'primary'}
                  leftIcon={<Mic className="h-4 w-4" />}
                  onClick={() => setIsDeviceDialogOpen(true)}
                >
                  {t('meetings.recordNow')}
                </Button>
              )}
              {canRecord && (
                <Button
                  variant="primary"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => setIsRecordDialogOpen(true)}
                >
                  {t('meetings.recordMeeting')}
                </Button>
              )}
            </>
          )
        }
        below={
          <MeetingFilters
            params={params}
            onChange={patch}
            onReset={() => setSearchParams(new URLSearchParams())}
          />
        }
      />

      <Card>
        <QueryBoundary
          isLoading={isPending}
          error={error}
          onRetry={refetch}
          skeleton={<SkeletonList rows={8} className="p-4" />}
          isEmpty={data?.items.length === 0}
          empty={
            <EmptyState
              icon={<CalendarClock />}
              title={t('meetings.noMatchTitle')}
              description={t('meetings.noMatchBody')}
              action={
                <Button variant="primary" onClick={() => setSearchParams(new URLSearchParams())}>
                  {t('common.clearFilters')}
                </Button>
              }
            />
          }
        >
          <ul>
            {data?.items.map((meeting) => (
              <MeetingListItem
                key={meeting.id}
                meeting={meeting}
                linkToSeriesWhenRecurring={isPlainBrowse}
              />
            ))}
          </ul>
        </QueryBoundary>
      </Card>

      {data && data.total > PAGE_SIZE && (
        <Pagination
          page={params.page ?? 1}
          pageSize={PAGE_SIZE}
          total={data.total}
          onPageChange={(page) => patch({ page })}
        />
      )}

      <Modal
        isOpen={isRecordDialogOpen}
        onClose={closeRecordDialog}
        title="Record a meeting"
        description="Choose a platform and enter the meeting link."
        footer={
          <>
            <Button variant="secondary" onClick={closeRecordDialog}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                (platform !== 'teams' && platform !== 'meet') ||
                !meetingUrl.trim() ||
                recordMeeting.isPending
              }
              onClick={() => {
                // Narrows `platform` past 'zoom', which stays disabled in the
                // dropdown below and unsupported by the API.
                if (platform !== 'teams' && platform !== 'meet') return
                recordMeeting.mutate(
                  {
                    platform,
                    meetingUrl: meetingUrl.trim(),
                    // Omitted rather than sent empty, so the server applies its
                    // own placeholder instead of storing a blank title.
                    ...(meetingTitle.trim()
                      ? { title: meetingTitle.trim() }
                      : {}),
                  },
                  { onSuccess: closeRecordDialog },
                )
              }}
            >
              {recordMeeting.isPending ? 'Requesting...' : 'Request recorder to join'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label
              htmlFor="recording-platform"
              className="mb-1.5 block text-sm font-medium text-fg"
            >
              Platform
            </label>

            <select
              id="recording-platform"
              value={platform}
              onChange={(event) =>
                setPlatform(event.target.value as RecordingPlatform)
              }
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg outline-none focus:border-brand"
            >
              <option value="teams">Microsoft Teams</option>
              <option value="meet">Google Meet</option>
              <option value="zoom" disabled>Zoom (coming soon)</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="meeting-url"
              className="mb-1.5 block text-sm font-medium text-fg"
            >
              Meeting URL
            </label>

            <input
              id="meeting-url"
              type="url"
              value={meetingUrl}
              onChange={(event) => setMeetingUrl(event.target.value)}
              placeholder="Paste the meeting link"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg outline-none placeholder:text-fg-subtle focus:border-brand"
            />
          </div>

          {/*
            OPTIONAL, AND THE ONLY CHANCE TO NAME THE MEETING. A pasted link
            carries no calendar subject, so without this every manually
            requested recording is filed under the server's placeholder and
            they are indistinguishable in the list. The local Docker path can
            recover a title from the joined meeting afterwards; the deployed
            one cannot -- it hands off at RunTask and never looks again.
          */}
          <div>
            <label
              htmlFor="meeting-title"
              className="mb-1.5 block text-sm font-medium text-fg"
            >
              Title <span className="text-fg-subtle">(optional)</span>
            </label>

            <input
              id="meeting-title"
              type="text"
              maxLength={200}
              value={meetingTitle}
              onChange={(event) => setMeetingTitle(event.target.value)}
              placeholder="What is this meeting about?"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg outline-none placeholder:text-fg-subtle focus:border-brand"
            />
          </div>
        </div>
      </Modal>

      {/*
        MOUNTED UNCONDITIONALLY, unlike the button. The recorder lives inside
        this component, and every token refresh resets the permission set to
        null until it is re-read -- so gating the mount on `canRecordNow`
        unmounted it, and stopped the recording, about once an hour.
      */}
      <DeviceRecorderDialog
        isOpen={isDeviceDialogOpen}
        onClose={() => setIsDeviceDialogOpen(false)}
      />
    </div>
  )
}
